import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../../../app/api/api';
import { fetchMealItemCustomizationsAsync } from './mealItemCustomizationSlice';
import {
  MealItemCustomizationFormData,
  MealItemCustomizationOptionFormData,
} from '../../../../app/data/models/mealItems/mealItemCustomizationFormData';
import { MealItemCustomizationRules } from '../../../../app/data/models/mealItems/mealItemCustomization';
import {
  toMealItemCustomizationModel,
  toMealItemCustomizationOptionModel,
} from '../../../../app/data/models/mealItems/mealItemCustomizationModel';

// State interface
export interface UpdateCustomizationState {
  status: string;
  dismiss: boolean;
}

// Initial state
const initialState: UpdateCustomizationState = {
  status: 'idle',
  dismiss: false,
};

// Thunk for updating customization
export const updateMealItemCustomizationAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    customizationId: string;
    data: MealItemCustomizationFormData;
  }
>(
  'mealItemCustomizations/update',
  async ({ restaurantId, customizationId, data }, thunkApi) => {
    try {
      await api.MealItemCustomization.update(
        restaurantId,
        customizationId,
        toMealItemCustomizationModel(data)
      );
      thunkApi.dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

// Thunk for updating customization option
export const updateMealItemCustomizationOptionAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    customizationId: string;
    optionId: string;
    data: MealItemCustomizationOptionFormData;
  }
>(
  'mealItemCustomizations/updateOption',
  async ({ restaurantId, customizationId, optionId, data }, thunkApi) => {
    try {
      await api.MealItemCustomization.updateOption(
        restaurantId,
        customizationId,
        optionId,
        toMealItemCustomizationOptionModel(data)
      );
      thunkApi.dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

// Thunk for creating customization option
export const createMealItemCustomizationOptionAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    customizationId: string;
    data: MealItemCustomizationOptionFormData;
  }
>(
  'mealItemCustomizations/createOption',
  async ({ restaurantId, customizationId, data }, thunkApi) => {
    try {
      await api.MealItemCustomization.createOption(
        restaurantId,
        customizationId,
        toMealItemCustomizationOptionModel(data)
      );
      thunkApi.dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

// Thunk for deleting customization option
export const deleteMealItemCustomizationOptionAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    customizationId: string;
    optionId: string;
  }
>(
  'mealItemCustomizations/deleteOption',
  async ({ restaurantId, customizationId, optionId }, thunkApi) => {
    try {
      await api.MealItemCustomization.deleteOption(
        restaurantId,
        customizationId,
        optionId
      );
      thunkApi.dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

// Thunk for updating customization rules
export const updateMealItemCustomizationRulesAsync = createAsyncThunk<
  void,
  {
    restaurantId: string;
    customizationId: string;
    data: MealItemCustomizationRules;
  }
>(
  'mealItemCustomizations/updateRules',
  async ({ restaurantId, customizationId, data }, thunkApi) => {
    try {
      await api.MealItemCustomization.updateRules(
        restaurantId,
        customizationId,
        data
      );
      thunkApi.dispatch(fetchMealItemCustomizationsAsync({ restaurantId }));
    } catch (error: unknown) {
      if (error instanceof Error) {
        return thunkApi.rejectWithValue({ error: error.message });
      }
      return thunkApi.rejectWithValue({ error: 'An unknown error occurred' });
    }
  }
);

// Slice
export const updateMealItemCustomizationSlice = createSlice({
  name: 'updateMealItemCustomization',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Update Customization
    builder.addCase(updateMealItemCustomizationAsync.pending, (state) => {
      state.status = 'pendingUpdateCustomization';
      state.dismiss = false;
    });
    builder.addCase(updateMealItemCustomizationAsync.fulfilled, (state) => {
      state.status = 'idle';
      state.dismiss = true;
    });
    builder.addCase(updateMealItemCustomizationAsync.rejected, (state) => {
      state.status = 'idle';
      state.dismiss = false;
    });

    // Update Option
    builder.addCase(updateMealItemCustomizationOptionAsync.pending, (state) => {
      state.status = 'pendingUpdateOption';
    });
    builder.addCase(
      updateMealItemCustomizationOptionAsync.fulfilled,
      (state) => {
        state.status = 'idle';
      }
    );
    builder.addCase(
      updateMealItemCustomizationOptionAsync.rejected,
      (state) => {
        state.status = 'idle';
      }
    );

    // Create Option
    builder.addCase(createMealItemCustomizationOptionAsync.pending, (state) => {
      state.status = 'pendingCreateOption';
    });
    builder.addCase(
      createMealItemCustomizationOptionAsync.fulfilled,
      (state) => {
        state.status = 'idle';
      }
    );
    builder.addCase(
      createMealItemCustomizationOptionAsync.rejected,
      (state) => {
        state.status = 'idle';
      }
    );

    // Delete Option
    builder.addCase(deleteMealItemCustomizationOptionAsync.pending, (state) => {
      state.status = 'pendingDeleteOption';
    });
    builder.addCase(
      deleteMealItemCustomizationOptionAsync.fulfilled,
      (state) => {
        state.status = 'idle';
      }
    );
    builder.addCase(
      deleteMealItemCustomizationOptionAsync.rejected,
      (state) => {
        state.status = 'idle';
      }
    );
  },
});

export default updateMealItemCustomizationSlice.reducer;
